import React, { useState, useCallback, useEffect } from "react";
import { Button, Modal, Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";
import { socketEvents } from "../../../settings/socketEventName";
import { useUserSelfStore } from "../../../store/apis/useUserStore";
import { useSocketEvents } from "../../../socket/hooks/useSocketEvents";
import { localStorageUtil } from "../../../utils/CommonFunctions";
import { useBridgeStore } from "../../../store/apis/useBridgeStore";

const ConfirmationModal = ({ isOpen, onClose, channelId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  // Members in the channel, checked when the dialog opens: the last member can't leave
  const [memberCount, setMemberCount] = useState(null);
  const { emit } = useSocketEvents();

  const userSelfData = useUserSelfStore((state) => state.userSelfData);
  const triggerDraftRefresh =  useBridgeStore(store => store.triggerDraftRefresh)
  const triggerSidebarRefresh = useBridgeStore((store) => store.triggerSidebarRefresh);
  const navigate = useNavigate();

  const updateUserDataAndNavigate = useCallback(
    (defaultChannelId) => {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      userData.lastActiveChat = {
        id: defaultChannelId,
        type: "channel",
      };

      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("current_id", defaultChannelId);
      navigate(`/elsnertech/channels/${defaultChannelId}`);
    },
    [navigate]
  );

  useEffect(() => {
    if (!isOpen || !channelId) return;
    let ignore = false;
    setMemberCount(null);
    ApiService.get(`${API_ENDPOINTS.getChannel}/${channelId}`)
      .then((response) => {
        if (!ignore) setMemberCount(response?.data?.members?.length ?? 0);
      })
      // If the count can't be read, let the server decide when Leave is pressed
      .catch(() => !ignore && setMemberCount(Infinity));
    return () => {
      ignore = true;
    };
  }, [isOpen, channelId]);

  const isLastMember = memberCount !== null && memberCount <= 1;

  const leaveChannelApi = useCallback(async () => {
    if (!channelId) {
      console.error("Channel ID is required");
      return;
    }

    setLoading(true);
    try {
      const response = await ApiService.put(
        `${API_ENDPOINTS.leaveChannelApi}/${channelId}`
      );

      if (response?.status === 1) {
        localStorageUtil.removeItem(`draft-${channelId}`);
        triggerDraftRefresh(Date.now());
        const defaultChannelId = localStorage.getItem("defaultChannelId");

        if (defaultChannelId) {
          updateUserDataAndNavigate(defaultChannelId);
        }

        let members = response?.data?.members.map((item) => item?.id);
        emit(socketEvents.LEAVE_CHANNEL, {
          data: {
            senderId: userSelfData?._id,
            receiverId: members,
            channelId: channelId,
          },
        });
        localStorageUtil.removeItem(`draft-${channelId}`);
        // The channel leaves the sidebar right away, wherever it was left from (the channel
        // header only refreshed the chat, so it stayed listed until a page reload)
        triggerSidebarRefresh(Date.now(), "all");
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error("Error leaving channel:", error);
      // e.g. "You are the last member of this channel, so you can't leave it."
      message.error(error?.response?.data?.message || "Couldn't leave the channel. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [channelId, updateUserDataAndNavigate, onSuccess, onClose, triggerSidebarRefresh]);

  const handleCancel = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  const modalFooter = isLastMember
    ? [
        <Button key="ok" type="primary" onClick={onClose} className="primary-btn">
          OK
        </Button>,
      ]
    : [
    <Button
      key="cancel"
      onClick={handleCancel}
      className="text-btn"
      disabled={loading}
    >
      Cancel
    </Button>,
    <Button
      key="leave"
      type="primary"
      onClick={leaveChannelApi}
      loading={loading}
      disabled={memberCount === null}
      className="danger-btn ant-btn"
    >
      Leave
    </Button>,
  ];

  return (
    <Modal
      title={isLastMember ? "You can't leave this channel" : "Confirm Leave Channel"}
      open={isOpen}
      onCancel={handleCancel}
      footer={modalFooter}
      className="ec-modal rename-channel-modal"
      width={600}
      style={{ top: 20 }}
      closable={!loading}
      maskClosable={!loading}
    >
      <div className="delete-msg-confirmation">
        {memberCount === null ? (
          <Spin size="small" />
        ) : isLastMember ? (
          <span>
            You are the last member of this channel, so you can't leave it. Add someone to the
            channel first if you want to leave.
          </span>
        ) : (
          <span>Are you sure you want to Leave this Channel?</span>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
